import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockVendorManagementService } from '@/services/mock/MockVendorManagementService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { VendorRecord, VendorStatusKey } from '@/types/domain/vendor-management';
import { DocumentAttachment, RequestComment } from '@/types/domain';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { animateFadeIn } from '@/lib/motion';
import { ROUTES } from '@/constants/routes';
import {
  ArrowLeft,
  FileText,
  MessageSquare,
  CheckCircle2,
  AlertTriangle,
  Building,
  CreditCard,
  Layers,
  ShieldCheck,
  Star,
  Send,
  ExternalLink,
} from 'lucide-react';
import { VendorCompliancePanel } from '@/components/vendors/VendorCompliancePanel';
import { VendorPerformanceCard } from '@/components/vendors/VendorPerformanceCard';
import { VendorActionModal } from '@/components/vendors/VendorActionModal';
import { DocumentManagementPanel } from '@/components/data-display/DocumentManagementPanel';
import { EnterpriseCommentThread } from '@/components/data-display/EnterpriseCommentThread';

export interface VendorDetailPageProps {
  recordId?: string;
  isDrawer?: boolean;
  onClose?: () => void;
}

export const VendorDetailPage: React.FC<VendorDetailPageProps> = ({
  recordId,
  isDrawer,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = recordId || paramId;
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);

  const [vendor, setVendor] = useState<VendorRecord | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [comments, setComments] = useState<RequestComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal State
  const [targetAction, setTargetAction] = useState<'APPROVE' | 'SUSPEND' | 'BLOCK' | 'REJECT'>('SUSPEND');
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'COMMERCIAL' | 'COMPLIANCE' | 'PERFORMANCE' | 'RFQS' | 'DOCUMENTS' | 'COMMENTS'>('OVERVIEW');

  const loadVendorDetail = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const detail = await mockVendorManagementService.getVendorById(id);
      if (!detail) {
        navigate(ROUTES.VENDORS.LIST);
        return;
      }
      setVendor(detail);

      const [docs, comms] = await Promise.all([
        mockDocumentCommentService.getDocuments('VENDOR', detail.vendorCode),
        mockDocumentCommentService.getComments('VENDOR', detail.vendorCode),
      ]);
      setDocuments(docs);
      setComments(comms);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    animateFadeIn(pageRef.current);
    loadVendorDetail();
  }, [id]);

  const handleOpenActionModal = (action: 'APPROVE' | 'SUSPEND' | 'BLOCK') => {
    setTargetAction(action);
    setIsActionModalOpen(true);
  };

  const handleConfirmStatusChange = async (newStatus: VendorStatusKey, reason: string) => {
    if (!vendor) return;
    try {
      setIsSubmittingAction(true);
      await mockVendorManagementService.updateVendorStatus(vendor.id, newStatus, reason);
      setIsActionModalOpen(false);
      await loadVendorDetail();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update vendor status');
    } finally {
      setIsSubmittingAction(false);
    }
  };

  if (isLoading || !vendor) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Vendor Command Center Workspace...
      </div>
    );
  }

  return (
    <div ref={pageRef} className={`space-y-4 font-poppins ${isDrawer ? 'pb-2' : 'pb-24 max-w-7xl mx-auto'}`}>
      {/* Top Header Bar */}
      <div className={`flex justify-between gap-3 border-b border-border/60 pb-4 ${isDrawer ? 'flex-col items-start' : 'flex-col sm:flex-row sm:items-center'}`}>
        <div className="flex items-center gap-3 min-w-0">
          {!isDrawer && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.VENDORS.LIST)}
              className="h-9 rounded-xl border-border font-bold font-poppins cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Vendor Directory
            </Button>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-poppins text-xl font-extrabold text-foreground">{vendor.vendorCode}</span>
            {vendor.sapVendorCode && (
              <span className="font-mono text-xs font-bold text-purple-600 bg-purple-500/10 px-2.5 py-0.5 rounded-lg border border-purple-500/20 whitespace-nowrap shadow-2xs">
                SAP: {vendor.sapVendorCode}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={() => navigate(ROUTES.RFQ.LIST)}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs font-poppins cursor-pointer"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" /> Invite to RFQ
          </Button>

          {vendor.status !== 'APPROVED' && vendor.status !== 'ACTIVE' && (
            <Button
              size="sm"
              onClick={() => handleOpenActionModal('APPROVE')}
              className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs font-poppins cursor-pointer"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve Vendor
            </Button>
          )}

          {vendor.status !== 'SUSPENDED' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleOpenActionModal('SUSPEND')}
              className="rounded-xl font-bold text-amber-700 border-amber-200 hover:bg-amber-500/10 font-poppins cursor-pointer"
            >
              <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Suspend
            </Button>
          )}

          <span
            className={`text-xs font-extrabold uppercase px-3 py-1 rounded-full font-poppins shadow-2xs whitespace-nowrap ${
              vendor.status === 'APPROVED' || vendor.status === 'ACTIVE'
                ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
                : 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
            }`}
          >
            {vendor.status}
          </span>
        </div>
      </div>

      {/* Main Vendor Header Card */}
      <Card className="border-border rounded-2xl shadow-xs font-poppins">
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-extrabold text-primary uppercase tracking-wider">{vendor.category}</span>
                <span className="text-[10px] text-foreground-muted">•</span>
                <span className="text-xs font-semibold text-foreground-secondary">{vendor.industry}</span>
              </div>
              <h1 className="font-poppins text-xl sm:text-2xl font-extrabold text-foreground">{vendor.name}</h1>
              <p className="text-xs text-foreground-secondary font-medium">Legal Name: <strong className="text-foreground">{vendor.legalName}</strong></p>
            </div>

            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-3.5 text-right space-y-1 min-w-[180px]">
              <span className="text-[11px] font-bold text-foreground-muted uppercase">Preferred Tier Rating</span>
              <div className="flex items-center justify-end gap-1 text-amber-500 font-extrabold text-xl">
                <Star className="h-5 w-5 fill-current" />
                <span>{vendor.commercial.preferredTier} TIER</span>
              </div>
            </div>
          </div>

          <div className={`grid text-xs font-medium ${isDrawer ? 'grid-cols-1 gap-3' : 'grid-cols-1 md:grid-cols-3 gap-4'}`}>
            <div>
              <span className="font-bold text-foreground-muted block text-[10px] uppercase">Primary Contact Person</span>
              <span className="font-bold text-foreground block">{vendor.primaryContact.name} ({vendor.primaryContact.role})</span>
              <span className="text-foreground-secondary font-mono">{vendor.primaryContact.email} • {vendor.primaryContact.phone}</span>
            </div>

            <div>
              <span className="font-bold text-foreground-muted block text-[10px] uppercase">Registered Address</span>
              <span className="font-semibold text-foreground block">{vendor.address.street}, {vendor.address.city}</span>
              <span className="text-foreground-secondary">{vendor.address.state}, {vendor.address.country} ({vendor.address.postalCode})</span>
            </div>

            <div>
              <span className="font-bold text-foreground-muted block text-[10px] uppercase">Tax & Compliance Status</span>
              <span className="font-mono font-bold text-foreground block">GST: {vendor.commercial.gstNumber}</span>
              <span className="font-mono font-bold text-foreground block">PAN: {vendor.commercial.panNumber}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Tabs with Modern Segmented Track */}
      <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-muted/40 border border-border/80 overflow-x-auto custom-scrollbar no-scrollbar">
        <button
          onClick={() => setActiveTab('OVERVIEW')}
          className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-extrabold transition-all whitespace-nowrap ${
            activeTab === 'OVERVIEW' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <Building className="h-4 w-4" /> Overview Profile
        </button>

        <button
          onClick={() => setActiveTab('COMMERCIAL')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all whitespace-nowrap ${
            activeTab === 'COMMERCIAL' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <CreditCard className="h-4 w-4" /> Commercial & Banking
        </button>

        <button
          onClick={() => setActiveTab('COMPLIANCE')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all whitespace-nowrap ${
            activeTab === 'COMPLIANCE' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <ShieldCheck className="h-4 w-4" /> Compliance Center ({vendor.compliance.length})
        </button>

        <button
          onClick={() => setActiveTab('PERFORMANCE')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all whitespace-nowrap ${
            activeTab === 'PERFORMANCE' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <Star className="h-4 w-4" /> Performance & Risk
        </button>

        <button
          onClick={() => setActiveTab('RFQS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all whitespace-nowrap ${
            activeTab === 'RFQS' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <Layers className="h-4 w-4" /> RFQ Sourcing History ({vendor.rfqHistory.length})
        </button>

        <button
          onClick={() => setActiveTab('DOCUMENTS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all whitespace-nowrap ${
            activeTab === 'DOCUMENTS' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <FileText className="h-4 w-4" /> Documents & Specs ({documents.length})
        </button>

        <button
          onClick={() => setActiveTab('COMMENTS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all whitespace-nowrap ${
            activeTab === 'COMMENTS' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <MessageSquare className="h-4 w-4" /> Activity & Discussions ({comments.length})
        </button>
      </div>

      {/* TAB 1: OVERVIEW */}
      {activeTab === 'OVERVIEW' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <VendorCompliancePanel complianceItems={vendor.compliance} />
          </div>

          <div>
            <VendorPerformanceCard performance={vendor.performance} riskRating={vendor.riskRating} />
          </div>
        </div>
      )}

      {/* TAB 2: COMMERCIAL & BANKING */}
      {activeTab === 'COMMERCIAL' && (
        <Card className="border-border rounded-2xl shadow-xs font-poppins">
          <CardHeader className="pb-3 border-b border-border/60">
            <CardTitle className="font-poppins text-xs font-bold text-foreground uppercase">
              Commercial Terms & Masked Banking Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="font-bold text-foreground-muted block text-[10px] uppercase">Default Payment Terms</span>
                <span className="font-semibold text-foreground">{vendor.commercial.paymentTerms}</span>
              </div>
              <div>
                <span className="font-bold text-foreground-muted block text-[10px] uppercase">Default Incoterms</span>
                <span className="font-semibold text-foreground">{vendor.commercial.incoterms}</span>
              </div>
              <div>
                <span className="font-bold text-foreground-muted block text-[10px] uppercase">Operating Currency</span>
                <span className="font-bold text-primary font-mono">{vendor.commercial.currency}</span>
              </div>
              <div>
                <span className="font-bold text-foreground-muted block text-[10px] uppercase">Preferred Supplier Tier</span>
                <span className="font-bold text-amber-600">{vendor.commercial.preferredTier} TIER</span>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-border bg-card space-y-1">
              <span className="font-bold text-foreground-muted block text-[10px] uppercase">Masked Banking Instrument</span>
              <div className="flex items-center justify-between font-mono">
                <span className="font-extrabold text-foreground text-sm">{vendor.commercial.maskedBankAccount}</span>
                <span className="text-xs text-foreground-secondary font-bold">{vendor.commercial.bankName}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TAB 3: COMPLIANCE CENTER */}
      {activeTab === 'COMPLIANCE' && <VendorCompliancePanel complianceItems={vendor.compliance} />}

      {/* TAB 4: PERFORMANCE & RISK */}
      {activeTab === 'PERFORMANCE' && <VendorPerformanceCard performance={vendor.performance} riskRating={vendor.riskRating} />}

      {/* TAB 5: RFQ SOURCING HISTORY */}
      {activeTab === 'RFQS' && (
        <Card className="border-border rounded-2xl shadow-xs font-poppins">
          <CardContent className="p-4 space-y-3">
            {vendor.rfqHistory.map((rfq) => (
              <div key={rfq.rfqId} className="p-4 rounded-xl border border-border/80 bg-card space-y-2 text-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/60 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-extrabold text-primary">{rfq.rfqId}</span>
                    <span className="font-bold text-foreground">{rfq.title}</span>
                  </div>

                  {rfq.quotationId && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(ROUTES.QUOTATIONS.COMPARISON(rfq.rfqId || 'rfq-2001'))}
                      className="rounded-xl font-bold h-8 text-xs text-purple-600 border-purple-200 hover:bg-purple-50 font-poppins cursor-pointer"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> View Quotation Matrix
                    </Button>
                  )}
                </div>

                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-secondary font-medium">Invited Date: {rfq.invitedAt}</span>
                  <span className="font-extrabold text-emerald-600">
                    Status: {rfq.invitationStatus} {rfq.quotedAmount ? `(Quoted: ₹${rfq.quotedAmount.toLocaleString('en-IN')})` : ''}
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* TAB 6: DOCUMENTS */}
      {activeTab === 'DOCUMENTS' && (
        <DocumentManagementPanel
          documents={documents}
          onUploadFile={async (file, cat) => {
            await mockDocumentCommentService.uploadDocument('VENDOR', vendor.vendorCode, file, cat);
            loadVendorDetail();
          }}
          onDownloadFile={async (docId) => {
            const res = await mockDocumentCommentService.downloadDocument(docId);
            window.open(res.downloadUrl, '_blank');
          }}
          onDeleteFile={async (docId) => {
            await mockDocumentCommentService.deleteDocument(docId);
            loadVendorDetail();
          }}
        />
      )}

      {/* TAB 7: COMMENTS */}
      {activeTab === 'COMMENTS' && (
        <div className="max-w-3xl">
          <EnterpriseCommentThread
            comments={comments}
            attachments={documents}
            onAddComment={async (text, attIds) => {
              await mockDocumentCommentService.createComment('VENDOR', vendor.vendorCode, text, attIds);
              loadVendorDetail();
            }}
            onUpdateComment={async (commentId, text) => {
              await mockDocumentCommentService.updateComment(commentId, text);
              loadVendorDetail();
            }}
            onDeleteComment={async (commentId) => {
              await mockDocumentCommentService.deleteComment(commentId);
              loadVendorDetail();
            }}
          />
        </div>
      )}

      {/* Action Modal */}
      <VendorActionModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        onConfirm={handleConfirmStatusChange}
        vendor={vendor}
        targetAction={targetAction}
        isSubmitting={isSubmittingAction}
      />
    </div>
  );
};
